import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { isEqual } from "lodash";
import { observable } from "mobx";

import {
  apiGetTasksWorkerQuicklookShareTaskId,
  apiGetTasksWorkerResultTaskId,
  apiGetTasksWorkerStatusTaskId,
  apiPostTasksWorkerQuicklookPatient,
  apiPostTasksWorkerQuicklookPpg,
  apiPutTasksWorkerQuicklookHeartcorePatientId,
  CeleryTaskStates,
  QuickLookTasks,
} from "../api";
import { ApiConnector } from "../api/api-connector";
import { assert } from "../utils/utils";

export type HeartCoreRangeMonths = 3 | 6 | 9 | 12;

export abstract class QuickLook {
  @observable accessor analysisType: QuickLookTasks | null = null;
  @observable accessor processing = false;
  @observable accessor resultUrl = "";
  @observable accessor taskId: string | null = null;
  @observable accessor sharedAnalysisUrl = "";
  @observable accessor sharePopupOpen = false;

  quicklookIntervalId?: ReturnType<typeof setInterval>;

  constructor(protected api: ApiConnector) {}

  quickLookURL() {
    return `${this.api.versionedRoute()}/tasks/worker/quicklook/${this.taskId}`;
  }

  reset() {
    this.processing = false;
    this.resultUrl = "";
    this.taskId = null;
    this.sharedAnalysisUrl = "";
    this.sharePopupOpen = false;
  }

  abstract submit(): Promise<void>;

  async getHandledException() {
    const responseException = await apiGetTasksWorkerResultTaskId({
      path: { task_id: this.requireTaskId() },
    });

    const reason = responseException.data.handled_exception
      ? `\n${responseException.data.handled_exception.title}: ${responseException.data.handled_exception.details}`
      : "";

    return i18n._(msg`Task with ID ${this.taskId ?? ""} failed.`) + reason;
  }

  checkStatus() {
    this.quicklookIntervalId = setInterval(async () => {
      const response = await apiGetTasksWorkerStatusTaskId({
        path: { task_id: this.requireTaskId() },
      });

      if (response.data.state === CeleryTaskStates.success) {
        clearInterval(this.quicklookIntervalId);
        this.taskReady();
      } else if (response.data.state === CeleryTaskStates.failure) {
        const alertText = await this.getHandledException();
        alert(alertText);
        this.reset();
        clearInterval(this.quicklookIntervalId);
      }
    }, 2000);
  }

  taskReady() {
    this.processing = false;
    this.resultUrl = this.quickLookURL();
  }

  async shareQuickLook() {
    const response = await apiGetTasksWorkerQuicklookShareTaskId({
      path: { task_id: this.requireTaskId() },
    });

    this.sharedAnalysisUrl = response.data.shareable_link;
    this.sharePopupOpen = true;
  }

  protected requireTaskId(): string {
    assert(
      this.taskId != null,
      "taskId is not set — the task has not been submitted",
    );
    return this.taskId;
  }

  protected requireAnalysisType(): QuickLookTasks {
    assert(this.analysisType != null, "analysisType is not set");
    return this.analysisType;
  }
}

export class PPGQuickLook extends QuickLook {
  @observable accessor uuids: string[];

  constructor(
    api: ApiConnector,
    analysisType: QuickLookTasks | null,
    uuids: string[],
  ) {
    super(api);
    this.analysisType = analysisType;
    this.uuids = [...uuids];
  }

  syncSelection(analysisType: QuickLookTasks | null, uuids: string[]) {
    if (this.analysisType === analysisType && isEqual(this.uuids, uuids)) {
      return;
    }

    this.analysisType = analysisType;
    this.uuids = [...uuids];
    this.reset();
  }

  async submit() {
    if (this.uuids.length > 10) {
      alert(i18n._(msg`More than 10 PPGs selected, currently not supported`));
      return;
    }
    this.processing = true;

    const response = await apiPostTasksWorkerQuicklookPpg({
      body: {
        task: this.requireAnalysisType(),
        uuids: this.uuids,
      },
    });
    this.taskId = response.data.task_id;
    this.checkStatus();
  }

  toggleUuid(uuid: string) {
    if (this.uuids.includes(uuid)) {
      this.uuids = this.uuids.filter((p) => p !== uuid);
    } else {
      this.uuids.push(uuid);
    }
  }
}

export class PatientQuickLook extends QuickLook {
  patientId: number;

  constructor(
    api: ApiConnector,
    analysisType: QuickLookTasks,
    patientId: number,
  ) {
    super(api);
    this.analysisType = analysisType;
    this.patientId = patientId;
  }

  async submit() {
    this.processing = true;

    const response = await apiPostTasksWorkerQuicklookPatient({
      body: {
        task: this.requireAnalysisType(),
        patient_ids: [this.patientId],
      },
    });
    this.taskId = response.data.task_id;
    this.checkStatus();
  }
}

export class HeartCoreQuickLook extends QuickLook {
  patientId: number;
  @observable accessor plotRangeMonths: HeartCoreRangeMonths;
  @observable accessor tableRangeMonths: HeartCoreRangeMonths;

  constructor(
    api: ApiConnector,
    patientId: number,
    plotRangeMonths: HeartCoreRangeMonths = 12,
    tableRangeMonths: HeartCoreRangeMonths = 3,
  ) {
    super(api);
    this.patientId = patientId;
    this.plotRangeMonths = plotRangeMonths;
    this.tableRangeMonths = tableRangeMonths;
  }

  async submit() {
    this.processing = true;

    const response = await apiPutTasksWorkerQuicklookHeartcorePatientId({
      path: { patient_id: this.patientId },
      query: {
        plot_range_months: this.plotRangeMonths,
        table_range_months: this.tableRangeMonths,
      },
    });
    this.taskId = response.data.task_id;
    this.checkStatus();
  }
}
